import { computed } from 'vue'
import { useQuery, useMutation } from './api'
import useAuthStore, { isUserToken } from '@/stores/authStore'
import type { Resource } from '@/types/_general'
import { useAccess } from './useAccess'
import { useUserExtendedData } from './useUserExtendedData'
import { toRaw } from 'vue'
import { computedAsync } from '@vueuse/core'
import { getPropertySchema, isReferenceObject, useResourceSwaggerDefinition } from './useSwagger'
import { trackDomainActivity } from '@/utils/userActivity'

export function useUserFavorites() {
  const authStore = useAuthStore()
  const access = useAccess()
  const userExtendedDataQuery = useUserExtendedData()
  const userFavoritesSchema = useResourceSwaggerDefinition('user--favorites')

  const userId = computed(() => {
    return isUserToken(authStore.selectedToken?.payload) ? authStore.selectedToken.payload.u_id : undefined
  })

  const canManageFavorites = computedAsync(async () => {
    if (userId.value == null) return false
    const [patch, attr] = await Promise.all([
      access.canCallMethodAsync('patch', '/user--favorites/{resource_id}'),
      access.canWriteAttributeAsync('user--favorites', 'favorites'),
    ])
    return patch && attr
  }, false)

  const resourcesWithFavorites = computed(() => {
    if (userFavoritesSchema.data.value == null) return []

    const favoritesProDef = getPropertySchema(userFavoritesSchema.data.value, 'favorites')
    if (isReferenceObject(favoritesProDef) || favoritesProDef.type !== 'object') return []

    return Object.keys(favoritesProDef.properties ?? {}) as Resource[]
  })

  const userFavoritesQuery = useQuery(
    '/user--favorites',
    computed(() => ({
      query: {
        where: {
          user: userId.value,
        },
      },
    })),
    {
      enabled: () => userId.value != null,
    }
  )
  const updateUserFavoritesMutation = useMutation('patch', '/user--favorites/{resource_id}')
  const createUserFavoritesMutation = useMutation('post', '/user--favorites')

  const userFavorites = computed(() => {
    return userFavoritesQuery.data.value?._items?.[0]?.favorites ?? {}
  })

  const favoriteIdsByResource = computed(() => {
    return Object.fromEntries(
      Object.entries(userFavorites.value).map(([resource, favorites]) => [
        resource,
        new Set(favorites.map((fav) => fav.id)),
      ])
    )
  })

  async function toggleFavorite(resource: Resource, item: { _id: string; reference_number?: string; name?: string }) {
    if (userId.value == null || !canManageFavorites.value) return
    await userExtendedDataQuery.suspense()

    const favorites = structuredClone(toRaw(userFavorites.value))
    favorites[resource] ??= []
    const favoriteCurrentIdx = favorites[resource].findIndex((fav) => fav.id === item._id)

    if (favoriteCurrentIdx === -1) {
      favorites[resource].push({
        id: item._id,
        added: new Date(),
        title: item.reference_number ?? item.name ?? item._id,
      })
      trackDomainActivity(resource, 'favorite')
    } else {
      favorites[resource].splice(favoriteCurrentIdx, 1)
      trackDomainActivity(resource, 'favorite_remove')
    }

    if (userFavoritesQuery.data.value?._items?.[0] == null) {
      if (!(await access.canCallMethodAsync('post', '/user--favorites'))) return
      createUserFavoritesMutation.mutate({
        // @ts-expect-error Swagger lies
        body: {
          ...(userExtendedDataQuery.data.value?.partner ? { partner: userExtendedDataQuery.data.value.partner } : {}),
          ...(userExtendedDataQuery.data.value?.account ? { account: userExtendedDataQuery.data.value.account } : {}),
          user: userId.value,
          favorites: favorites,
        },
      })
    } else {
      updateUserFavoritesMutation.mutate({
        path: { resource_id: userFavoritesQuery.data.value._items[0]._id },
        header: { 'If-Match': userFavoritesQuery.data.value._items[0]._etag },
        // @ts-expect-error Swagger lies
        body: {
          favorites: favorites,
        },
      })
    }
  }

  return {
    isLoading: userFavoritesQuery.isLoading,

    userFavorites,
    favoriteIdsByResource,
    canManageFavorites,
    resourcesWithFavorites,

    toggleFavorite,
  }
}
